import React, { useState, useEffect, useCallback } from "react";
import styles from "./Supervisor.module.css";
import { authenticatedFetch } from "../utils/authenticatedFetch";

interface Invitation {
    id: string;
    supervisor_id: string;
    email: string;
    token: string;
    status: "pending" | "accepted" | "declined" | "expired" | "cancelled";
    invited_at: string;
    accepted_at: string | null;
    expires_at: string | null;
}

interface Props {
    supervisorId: string;
    onInvitationChange: () => void;
}

const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5001";

const SupervisorInvitations: React.FC<Props> = ({ supervisorId, onInvitationChange }) => {
    const [invitations, setInvitations] = useState<Invitation[]>([]);
    const [loading, setLoading] = useState(true);
    const [emailInput, setEmailInput] = useState("");
    const [sending, setSending] = useState(false);
    const [actionLoading, setActionLoading] = useState<string | null>(null);

    const fetchInvitations = useCallback(async () => {
        try {
            const res = await authenticatedFetch(`${apiUrl}/api/supervisor/invitations?supervisor_id=${supervisorId}`);
            if (res.ok) {
                setInvitations(await res.json());
            }
        } catch (err) {
            console.error("Failed to fetch invitations", err);
        } finally {
            setLoading(false);
        }
    }, [supervisorId]);

    useEffect(() => {
        fetchInvitations();
    }, [fetchInvitations]);

    const handleInvite = async () => {
        const emails = emailInput
            .split(/[,;\n]+/)
            .map((e) => e.trim())
            .filter(Boolean);

        if (emails.length === 0) return;

        setSending(true);
        try {
            if (emails.length === 1) {
                const res = await authenticatedFetch(`${apiUrl}/api/supervisor/invite`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ supervisor_id: supervisorId, email: emails[0] }),
                });
                const data = await res.json();
                if (res.ok) {
                    (window as any).showNotification?.("Invitation sent!", "success");
                    setEmailInput("");
                } else {
                    (window as any).showNotification?.(data.error || "Failed to send invitation", "error");
                }
            } else {
                const res = await authenticatedFetch(`${apiUrl}/api/supervisor/invite-bulk`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ supervisor_id: supervisorId, emails }),
                });
                const data = await res.json();
                if (res.ok) {
                    const sent = data.results.filter((r: any) => r.status === "sent").length;
                    const failed = data.results.filter((r: any) => r.status === "error").length;
                    if (failed > 0) {
                        const errors = data.results
                            .filter((r: any) => r.status === "error")
                            .map((r: any) => `${r.email}: ${r.error}`)
                            .join("\n");
                        (window as any).showNotification?.(
                            `${sent} invitation(s) sent, ${failed} failed.`,
                            failed === emails.length ? "error" : "success"
                        );
                        console.warn("Bulk invite errors:", errors);
                    } else {
                        (window as any).showNotification?.(`${sent} invitation(s) sent!`, "success");
                    }
                    setEmailInput("");
                } else {
                    (window as any).showNotification?.(data.error || "Failed to send invitations", "error");
                }
            }
            fetchInvitations();
            onInvitationChange();
        } catch (err) {
            (window as any).showNotification?.("Failed to send invitation", "error");
        } finally {
            setSending(false);
        }
    };

    const handleResend = async (invitation: Invitation) => {
        setActionLoading(invitation.id);
        try {
            const res = await authenticatedFetch(`${apiUrl}/api/supervisor/invitations/${invitation.id}/resend`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ supervisor_id: supervisorId }),
            });
            if (res.ok) {
                (window as any).showNotification?.("Invitation resent!", "success");
                fetchInvitations();
            } else {
                const data = await res.json();
                (window as any).showNotification?.(data.error || "Failed to resend", "error");
            }
        } catch {
            (window as any).showNotification?.("Failed to resend invitation", "error");
        } finally {
            setActionLoading(null);
        }
    };

    const handleCancel = async (invitation: Invitation) => {
        setActionLoading(invitation.id);
        try {
            const res = await authenticatedFetch(`${apiUrl}/api/supervisor/invitations/${invitation.id}`, {
                method: "DELETE",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ supervisor_id: supervisorId }),
            });
            if (res.ok) {
                (window as any).showNotification?.("Invitation cancelled", "success");
                fetchInvitations();
                onInvitationChange();
            } else {
                const data = await res.json();
                (window as any).showNotification?.(data.error || "Failed to cancel", "error");
            }
        } catch {
            (window as any).showNotification?.("Failed to cancel invitation", "error");
        } finally {
            setActionLoading(null);
        }
    };

    const formatDate = (date: string | null) => {
        if (!date) return "-";
        try {
            return new Date(date).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
            });
        } catch {
            return "-";
        }
    };

    const getStatusBadge = (status: string) => {
        const classMap: Record<string, string> = {
            pending: styles.badgePending,
            accepted: styles.badgeAccepted,
            expired: styles.badgeExpired,
            cancelled: styles.badgeCancelled,
            declined: styles.badgeExpired,
        };
        return `${styles.badge} ${classMap[status] || ""}`;
    };

    if (loading) {
        return (
            <div className={styles.loadingContainer}>
                <div className={styles.loadingSpinner} />
                <p>Loading invitations...</p>
            </div>
        );
    }

    return (
        <div>
            {/* Invite Form */}
            <div className={styles.inviteForm}>
                <div className={styles.inviteFormTitle}>Invite Students</div>
                <div className={styles.inviteFormRow}>
                    <input
                        className={styles.inviteInput}
                        type="text"
                        placeholder="Enter email address(es), separated by commas"
                        value={emailInput}
                        onChange={(e) => setEmailInput(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && !sending) handleInvite();
                        }}
                    />
                    <button
                        className={styles.primaryButton}
                        onClick={handleInvite}
                        disabled={sending || !emailInput.trim()}
                    >
                        {sending ? "Sending..." : "Send Invitation"}
                    </button>
                </div>
                <p className={styles.inviteHelp}>
                    Separate multiple emails with commas. Students will receive an email with a link to join.
                </p>
            </div>

            {/* Invitations List */}
            <div className={styles.tableContainer}>
                <div className={styles.tableHeader}>
                    <span className={styles.tableTitle}>Invitations</span>
                </div>

                {invitations.length === 0 ? (
                    <div className={styles.emptyState}>
                        <p>No invitations sent yet.</p>
                    </div>
                ) : (
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>Email</th>
                                <th>Status</th>
                                <th>Sent</th>
                                <th>Expires</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {invitations.map((inv) => (
                                <tr key={inv.id}>
                                    <td>{inv.email}</td>
                                    <td>
                                        <span className={getStatusBadge(inv.status)}>{inv.status}</span>
                                    </td>
                                    <td>{formatDate(inv.invited_at)}</td>
                                    <td>{inv.status === "pending" ? formatDate(inv.expires_at) : "-"}</td>
                                    <td>
                                        <div className={styles.actionButtons}>
                                            {(inv.status === "pending" || inv.status === "expired") && (
                                                <button
                                                    className={`${styles.secondaryButton} ${styles.smallButton}`}
                                                    onClick={() => handleResend(inv)}
                                                    disabled={actionLoading === inv.id}
                                                >
                                                    {actionLoading === inv.id ? "..." : "Resend"}
                                                </button>
                                            )}
                                            {inv.status === "pending" && (
                                                <button
                                                    className={`${styles.dangerButton} ${styles.smallButton}`}
                                                    onClick={() => handleCancel(inv)}
                                                    disabled={actionLoading === inv.id}
                                                >
                                                    Cancel
                                                </button>
                                            )}
                                            {inv.status === "accepted" && (
                                                <span style={{ fontSize: "0.8125rem", color: "var(--Colors-Neutral-500)" }}>
                                                    Accepted {formatDate(inv.accepted_at)}
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default SupervisorInvitations;
